import type { ListBuilderConfiguration } from '../types';

/**
 * Location Item list configuration
 * Shows items at a specific location with quantity information
 * Converted from JSON to TypeScript for type safety
 */
export const LOCATION_ITEM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-location-items-all',
			cacheTTL: 600000,
			name: 'All Location Items',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'image',
					width: 84,
					fields: [
						{
							id: 'item_image',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'primaryImageUrl', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								imageOptions: { size: 'small', enableViewer: true },
							},
							displayStyle: 'image',
						},
					],
					headers: [{ id: 'image_header', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'item-info',
					label: 'Item',
					width: 250,
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'manufacturer', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sku-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId2', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Item' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
					],
					verticalAlignment: 'middle',
				},
				{
					id: 'category',
					label: 'Category',
					width: 180,
					fields: [
						{
							id: 'category',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'category', objectTable: 'item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Category' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'category', objectTable: 'item' },
					],
					verticalAlignment: 'middle',
				},
				{
					id: 'quantity',
					fields: [
						{
							id: 'quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantity', objectTable: 'location_item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Quantity' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantity',
							objectTable: 'location_item',
						},
					],
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'middle',
				},
				{
					// Distribution refactor (Phase B): per-(location, item)
					// capacity is the zone capacity used to convert
					// sensor fill % → unit count. Shows raw count alongside
					// quantity so the operator can see how full the zone is.
					id: 'capacity',
					fields: [
						{
							id: 'capacity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'capacity', objectTable: 'location_item' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-capacity', text: 'Capacity' }],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'capacity',
							objectTable: 'location_item',
						},
					],
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'middle',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			description:
				'Complete list of all items at a location with quantity information',
			defaultSortId: 'column-item-info-asc',
			requiredFields: [
				'_id',
				'location_item_id',
				'locationId',
				'itemId',
				'quantity',
				'capacity',
				'description',
				'manufacturer',
				'externalId2',
				'category',
				'primaryImageUrl',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'externalId2', fieldLabel: 'SKU' },
				{ fieldName: 'primaryImageUrl', fieldLabel: 'Image' },
			],
		},
		{
			baseUrl: '',
			objectLabel: 'Location Item',
			objectTable: 'location_item',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'locationId', fieldLabel: 'Location ID' },
				{ fieldName: 'itemId', fieldLabel: 'Item ID' },
				{ fieldName: 'quantity', fieldLabel: 'Quantity' },
				{ fieldName: 'capacity', fieldLabel: 'Capacity' },
			],
		},
	],
	tableSorts: [
		{
			id: 'description',
			name: 'Item Name',
			fields: [
				{ direction: 'ASC', fieldName: 'description', objectTable: 'item' },
			],
			description: 'Sort by item description',
		},
		{
			id: 'quantity',
			name: 'By Quantity',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'quantity',
					objectTable: 'location_item',
				},
			],
			description: 'Sort by quantity (highest first)',
		},
	],
};
